import { useState, useMemo } from 'react';

import { useMutation } from '@apollo/client';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

import CustomDrawer from '../../../../components/containers/CustomDrawer';
import { UPDATE_ORDER_ADDRESS } from '../../../../gql/orders';
import { isInDeliveryApps } from '../../../../lib/integratedCarrierApps';
import { ORDER_STATUSES } from '../../../../lib/orderStatuses';
import OrderAddressForm from './OrderAddressForm';
import OrderAddressMap from './OrderAddressMap';

const UNKNOWN = 'desconocido';
const DELIVERY_STATUS_ERROR = 'error';

function getAddressAlarm({ isVerified, isPartialMatch, systemName }) {
  switch (true) {
    case isInDeliveryApps({ systemName }):
      return {
        isVerifiedByOps: false,
        isVerifiedByGoogle: false,
        missingVerification: false,
      };
    case isVerified:
      return {
        isVerifiedByOps: true,
        isVerifiedByGoogle: false,
        missingVerification: false,
      };
    case isPartialMatch && !isVerified:
      return {
        isVerifiedByOps: false,
        isVerifiedByGoogle: false,
        missingVerification: true,
      };
    default:
      return {
        isVerifiedByOps: false,
        isVerifiedByGoogle: true,
        missingVerification: false,
      };
  }
}

function OrderAddress({
  id,
  systemName,
  sourceOrderId,
  dropOffAddress,
  status,
  deliveryStatus,
}) {
  const { computedAddress, sourceAddress } = dropOffAddress || {};
  const { isVerified, isVerifiedBy, isVerifiedAt, isPartialMatch, reference } =
    computedAddress || {};
  const {
    register,
    formState: { errors, defaultValues },
    handleSubmit,
    watch,
    reset: resetForm,
    control,
    setValue,
  } = useForm();

  const [showForm, setShowForm] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const computedAddressCoordinates = computedAddress?.location?.coordinates;
  const customComputedAddressLabel = useMemo(() => {
    const {
      street,
      addressNumber,
      municipality,
      subRegion,
      region,
      country,
      postalCode,
      label,
    } = computedAddress;
    return [
      street || label,
      addressNumber,
      municipality,
      subRegion,
      region,
      country,
      postalCode,
    ]
      .filter(Boolean)
      .join(', ');
  }, [computedAddress]);

  const [updateOrderAddress, { loading, error, reset }] = useMutation(
    UPDATE_ORDER_ADDRESS,
    {
      onError: () => {},
      onCompleted: () => setShowForm(false),
    },
  );
  const onClickUpdateAddress = (formData) => {
    const { complement, addressNumber, municipality, postalCode } = formData;

    const newComputedAddress = {
      ...formData.computedAddress,
      postalCode: postalCode || '',
      municipality: municipality || formData.computedAddress.municipality,
      addressNumber: addressNumber || formData.computedAddress.addressNumber,
      complement,
    };
    updateOrderAddress({
      variables: { id, computedAddress: newComputedAddress },
    });
  };
  const onClickShowForm = () => {
    // Pre-load the current computed address as a suggestion so the operator
    // sees the form, inputs and map already populated without searching again.
    // Only keep the fields accepted by ComputedAddressInput to avoid sending
    // Apollo metadata (__typename, id, verification flags) in the mutation.
    const suggestedComputedAddress = {
      street: computedAddress.street || '',
      addressNumber: computedAddress.addressNumber || '',
      municipality: computedAddress.municipality || '',
      subRegion: computedAddress.subRegion || '',
      region: computedAddress.region || '',
      country: computedAddress.country || '',
      postalCode: computedAddress.postalCode || '',
      label: customComputedAddressLabel || computedAddress.label || '',
      ...(computedAddressCoordinates
        ? {
            location: {
              type: 'Point',
              coordinates: computedAddressCoordinates,
            },
          }
        : {}),
    };
    resetForm({
      shippingAddressLabel: suggestedComputedAddress.label,
      computedAddress: suggestedComputedAddress,
      complement: computedAddress.complement || '',
    });
    setShowForm(true);
  };
  const onClickHideForm = () => {
    reset();
    setShowForm(false);
  };
  const onSubmit = handleSubmit(onClickUpdateAddress);
  const canUpdateAddress = useMemo(() => {
    const orderStatuses = Object.keys(ORDER_STATUSES);
    const isBeforeReadyAtStore =
      orderStatuses.indexOf(ORDER_STATUSES.readyAtStore) >
      orderStatuses.indexOf(ORDER_STATUSES[status]);
    const isShipmentRetryable =
      deliveryStatus === DELIVERY_STATUS_ERROR &&
      status === ORDER_STATUSES.invoiced;
    return (
      (isBeforeReadyAtStore || isShipmentRetryable) &&
      !isInDeliveryApps({ systemName })
    );
  }, [status, systemName, deliveryStatus]);
  const { isVerifiedByOps, isVerifiedByGoogle, missingVerification } = useMemo(
    () => getAddressAlarm({ isVerified, isPartialMatch, systemName }),
    [isVerified, isPartialMatch, systemName],
  );
  const isVerifiedAtMessage = useMemo(() => {
    if (!isVerifiedAt) {
      return '';
    }
    return `(${isVerifiedAt})`;
  }, [isVerifiedAt]);
  return (
    <Box
      id={`update-order-address-id-${id}`}
      key={`update-order-address-key-${id}`}
      sx={{ display: 'flex', alignItems: 'center', width: 200 }}
    >
      <Typography
        variant="caption"
        color={missingVerification ? 'error' : undefined}
      >
        {missingVerification
          ? sourceAddress
          : customComputedAddressLabel || computedAddress.label}
      </Typography>
      {isVerifiedByOps && (
        <Tooltip
          title={`Verificación manual por ${
            isVerifiedBy || UNKNOWN
          } ${isVerifiedAtMessage}`}
        >
          <DoneAllIcon color="success" />
        </Tooltip>
      )}
      {isVerifiedByGoogle && (
        <Tooltip title="Verificada por sistema">
          <DoneIcon color="success" />
        </Tooltip>
      )}
      {missingVerification && (
        <Tooltip title="Verificación necesaria, es probable que exista un error en la dirección">
          <PriorityHighIcon color="error" />
        </Tooltip>
      )}
      {!missingVerification && (
        <Tooltip title="Ver mapa">
          <IconButton
            id="viewMapButton"
            variant="body2"
            onClick={() => setShowMap(true)}
          >
            <MapOutlinedIcon />
          </IconButton>
        </Tooltip>
      )}
      {!showForm && canUpdateAddress && (
        <Tooltip title="Actualizar dirección de entrega">
          <IconButton
            id="invoiceButton"
            variant="body2"
            onClick={onClickShowForm}
            target="_blank"
            rel="noopener noreferrer"
          >
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
      )}
      <CustomDrawer
        drawerKey={`update-order-address-drawer-key${id}`}
        title={<Typography>#Orden: {sourceOrderId}</Typography>}
        open={showForm}
        onClose={onClickHideForm}
        width={500}
      >
        <Typography variant="h5">Actualizar dirección de entrega</Typography>
        <Typography>
          Dirección ingresada por el usuario: {sourceAddress}
        </Typography>
        {reference && (
          <Typography>
            Referencia ingresada por el usuario: {reference}
          </Typography>
        )}
        <OrderAddressForm
          register={register}
          watch={watch}
          control={control}
          errors={errors}
          onSubmit={onSubmit}
          loading={loading}
          mutationError={error}
          defaultValues={defaultValues}
          setValue={setValue}
          resetForm={resetForm}
          suggestedCoordinates={computedAddressCoordinates}
        />
      </CustomDrawer>
      <CustomDrawer
        drawerKey={`view-order-address-map-drawer-key${id}`}
        title={<Typography>#Orden: {sourceOrderId}</Typography>}
        open={showMap}
        onClose={() => setShowMap(false)}
        width={500}
      >
        <Typography variant="h5">Dirección de entrega</Typography>
        <OrderAddressMap
          computedAddressLabel={customComputedAddressLabel}
          sourceAddress={sourceAddress}
          reference={reference}
          coordinates={computedAddressCoordinates}
          isVerified={isVerified}
          isVerifiedBy={isVerifiedBy}
          isVerifiedAt={isVerifiedAt}
          isPartialMatch={isPartialMatch}
        />
      </CustomDrawer>
    </Box>
  );
}
OrderAddress.propTypes = {
  id: PropTypes.string.isRequired,
  systemName: PropTypes.string.isRequired,
  sourceOrderId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  deliveryStatus: PropTypes.string,
  dropOffAddress: PropTypes.shape({
    sourceAddress: PropTypes.string,
    computedAddress: PropTypes.shape({
      addressNumber: PropTypes.string,
      country: PropTypes.string,
      label: PropTypes.string,
      street: PropTypes.string,
      complement: PropTypes.string,
      reference: PropTypes.string,
      postalCode: PropTypes.string,
      municipality: PropTypes.string,
      region: PropTypes.string,
      subRegion: PropTypes.string,
      isVerified: PropTypes.bool,
      isVerifiedBy: PropTypes.string,
      isVerifiedAt: PropTypes.string,
      isPartialMatch: PropTypes.bool,
      location: PropTypes.shape({
        type: PropTypes.string,
        coordinates: PropTypes.arrayOf(PropTypes.number),
      }),
    }),
    setValue: PropTypes.func,
  }).isRequired,
};
OrderAddress.defaultProps = {
  deliveryStatus: undefined,
};
export default OrderAddress;
